import { Link, useNavigate, useParams, useSearchParams } from "react-router";
import { useQuery } from "@tanstack/react-query";

import { fetchPremiumPaymentDetail } from "../../utils/apiUtils";
import { money } from "./financeShared";
import "./PremiumPaymentDetail.scss";

const accountHref = (payment, path) => {
    if (!payment?.accountKey) return path;
    const params = new URLSearchParams();
    params.set("accountKey", String(payment.accountKey));
    if (payment.accountHccId) params.set("accountHccId", payment.accountHccId);
    if (payment.accountName) params.set("accountName", payment.accountName);
    return `${path}?${params.toString()}`;
};

const billHref = (billKey, asOf) => {
    if (!billKey) return null;
    const qs = asOf ? `?asOf=${encodeURIComponent(asOf)}` : "";
    return `/finance/bills/${billKey}${qs}`;
};

const allocationLabel = (amount, allocated) => {
    const a = Number(amount) || 0;
    const al = Number(allocated) || 0;
    if (a <= 0.005) return { label: "No amount", tone: "muted" };
    if (al <= 0.005) return { label: "Unallocated", tone: "warn" };
    if (al >= a - 0.005) return { label: "Fully allocated", tone: "ok" };
    return { label: "Partially allocated", tone: "warn" };
};

const MoneyAmount = ({ value, className = "num" }) => {
    if (value == null || !Number.isFinite(Number(value))) {
        return <span className={className}>—</span>;
    }
    return <span className={className}>{money(value)}</span>;
};

const PremiumPaymentDetail = () => {
    const navigate = useNavigate();
    const { paymentKey } = useParams();
    const [searchParams] = useSearchParams();
    const asOf = searchParams.get("asOf") || undefined;

    const { data, isLoading, error } = useQuery({
        queryKey: ["premium-payment-detail", paymentKey, asOf || null],
        queryFn: () =>
            fetchPremiumPaymentDetail({
                paymentKey,
                asOfDate: asOf,
            }),
        enabled: !!paymentKey,
        staleTime: 30_000,
        retry: false,
    });

    const payment = data?.payment;
    const allocations = Array.isArray(data?.allocations) ? data.allocations : [];
    const unallocated =
        payment != null
            ? Math.max(0, (Number(payment.amount) || 0) - (Number(payment.allocatedAmount) || 0))
            : null;
    const allocState = payment
        ? allocationLabel(payment.amount, payment.allocatedAmount)
        : null;

    if (isLoading) {
        return (
            <div className="container premium-payment-detail">
                <p className="muted">Loading premium payment…</p>
            </div>
        );
    }

    if (error || data?.error || !payment) {
        return (
            <div className="container premium-payment-detail">
                <p className="danger">
                    {error?.message || data?.error || "Premium payment not found"}
                </p>
                <button
                    type="button"
                    className="premium-payment-detail__link"
                    onClick={() => navigate("/finance/payments")}
                >
                    Back to Premium Payments
                </button>
            </div>
        );
    }

    const title = payment.paymentNumber
        ? `Payment ${payment.paymentNumber}`
        : `Payment #${payment.key}`;

    return (
        <div className="container premium-payment-detail">
            <div className="premium-payment-detail__toolbar">
                <div className="premium-payment-detail__heading">
                    <Link
                        to={accountHref(payment, "/finance/payments")}
                        className="premium-payment-detail__link"
                    >
                        ← Premium Payments
                    </Link>
                    <p className="title">{title}</p>
                    <p className="sub-title">
                        {payment.accountName || payment.accountHccId || "Account"}
                        {payment.accountHccId ? ` · ${payment.accountHccId}` : ""}
                        {payment.paymentType ? ` · ${payment.paymentType}` : ""}
                    </p>
                </div>
                <div className="premium-payment-detail__toolbar-actions">
                    {!!payment.accountKey && (
                        <Link
                            to={accountHref(payment, "/finance/bills")}
                            className="btn premium-payment-detail__btn"
                        >
                            Premium bills
                        </Link>
                    )}
                    {!!payment.sentForBillKey && (
                        <Link
                            to={billHref(payment.sentForBillKey, asOf)}
                            className="btn premium-payment-detail__btn"
                        >
                            Sent-for bill #{payment.sentForBillKey}
                        </Link>
                    )}
                </div>
            </div>

            <article className="premium-payment-detail__card">
                <section className="premium-payment-detail__summary">
                    <div>
                        <span className="premium-payment-detail__meta-label">Payment date</span>
                        <strong>{payment.paymentDate || "—"}</strong>
                    </div>
                    <div>
                        <span className="premium-payment-detail__meta-label">Received</span>
                        <strong>{payment.receiptDate || "—"}</strong>
                    </div>
                    <div>
                        <span className="premium-payment-detail__meta-label">Processed</span>
                        <strong>{payment.processDate || "—"}</strong>
                    </div>
                    <div>
                        <span className="premium-payment-detail__meta-label">Amount</span>
                        <MoneyAmount value={payment.amount} className="" />
                    </div>
                    <div>
                        <span className="premium-payment-detail__meta-label">Allocated</span>
                        <MoneyAmount value={payment.allocatedAmount} className="" />
                    </div>
                    <div className="premium-payment-detail__summary-due">
                        <span className="premium-payment-detail__meta-label">Unallocated</span>
                        <MoneyAmount value={unallocated} className="" />
                    </div>
                </section>

                <div className="premium-payment-detail__flags">
                    {allocState ? (
                        <span
                            className={`premium-payment-detail__status premium-payment-detail__status--${allocState.tone}`}
                        >
                            {allocState.label}
                        </span>
                    ) : null}
                    {payment.isReconciled ? (
                        <span className="premium-payment-detail__status premium-payment-detail__status--ok">
                            Reconciled
                        </span>
                    ) : (
                        <span className="premium-payment-detail__status premium-payment-detail__status--muted">
                            Not reconciled
                        </span>
                    )}
                    {payment.isReversal ? (
                        <span className="premium-payment-detail__status premium-payment-detail__status--warn">
                            Reversal
                        </span>
                    ) : null}
                    {payment.isVoid ? (
                        <span className="premium-payment-detail__status premium-payment-detail__status--danger">
                            Void
                        </span>
                    ) : null}
                    {payment.transactNum ? (
                        <span className="premium-payment-detail__status premium-payment-detail__status--muted">
                            Txn {payment.transactNum}
                        </span>
                    ) : null}
                    <span className="premium-payment-detail__status premium-payment-detail__status--muted">
                        ID {payment.key}
                    </span>
                </div>

                <section className="premium-payment-detail__section">
                    <div className="premium-payment-detail__section-head">
                        <h2>Allocations to bills</h2>
                        <span className="premium-payment-detail__count">
                            {allocations.length}{" "}
                            {allocations.length === 1 ? "line" : "lines"}
                        </span>
                    </div>
                    {allocations.length === 0 ? (
                        <p className="muted">This payment is not allocated to any bill yet.</p>
                    ) : (
                        <div className="premium-payment-detail__table-wrap">
                            <table>
                                <thead>
                                    <tr>
                                        <th>Bill</th>
                                        <th>Plan</th>
                                        <th>Category</th>
                                        <th>Date</th>
                                        <th className="num">Amount</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {allocations.map((a) => {
                                        const href = billHref(a.billKey, asOf);
                                        return (
                                            <tr key={a.key}>
                                                <td>
                                                    {href ? (
                                                        <Link to={href}>Bill #{a.billKey}</Link>
                                                    ) : (
                                                        "—"
                                                    )}
                                                </td>
                                                <td>
                                                    {a.planName || a.planHccId || "—"}
                                                    {a.planName && a.planHccId ? (
                                                        <div className="premium-payment-detail__muted">
                                                            {a.planHccId}
                                                        </div>
                                                    ) : null}
                                                </td>
                                                <td>
                                                    {a.categoryName || a.categoryCode || "—"}
                                                    {a.categoryName && a.categoryCode ? (
                                                        <div className="premium-payment-detail__muted">
                                                            {a.categoryCode}
                                                        </div>
                                                    ) : null}
                                                </td>
                                                <td>{a.date || "—"}</td>
                                                <td className="num">
                                                    <MoneyAmount value={a.amount} />
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>
                    )}
                </section>
            </article>
        </div>
    );
};

export default PremiumPaymentDetail;
